import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import Logo from "./Logo";
import { useUserContext } from "../UserProvider";
import ProgressBar from "../Home/ProgressBar";
import { sendRequest } from "../../api";

const GeneratingSummary = ({ progress = 0, error = null, loadingTitle = "Generating summary..." }) => {
  const navigate = useNavigate();

  const handleRetry = async () => {
    window.location.reload(); // This will restart the generation process
  };

  const handleSkip = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#f8f7f2] p-2">
      <div className="w-full max-w-[702px] flex flex-col">
        <Logo />
        <div className="w-full bg-white rounded-lg shadow-sm border border-[#e7e6e3] flex-col justify-start items-start flex overflow-hidden">
          <div className="self-stretch p-3 justify-between items-center inline-flex">
            <div className="grow shrink basis-0 h-8 justify-start items-center gap-2 flex" />
            <div className="justify-start items-center gap-1 flex">
              <div className="w-8 h-8 bg-[#1b1a17] rounded-full border border-[#1b1a17] flex items-center justify-center">
                <div className="text-white text-base font-bold font-inter">1</div>
              </div>
              <div className="w-8 py-4 flex-col justify-start items-start gap-2.5 inline-flex">
                <div className="self-stretch h-px border border-[#1b1a17]" />
              </div>
              <div className="w-8 h-8 bg-[#1b1a17] rounded-full border border-[#1b1a17] flex items-center justify-center">
                <div className="text-white text-base font-bold font-inter">2</div>
              </div>
              <div className="w-8 py-4 flex-col justify-start items-start gap-2.5 inline-flex">
                <div className="self-stretch h-px border border-[#1b1a17]" />
              </div>
              <div className="w-8 h-8 bg-[#1b1a17] rounded-full border border-[#1b1a17] flex items-center justify-center">
                <div className="text-white text-base font-bold font-inter">3</div>
              </div>
            </div>
            <div className="grow shrink basis-0 h-8 justify-end items-center gap-2 flex" />
          </div>
          
          <div className="self-stretch p-8 flex-col justify-center items-center gap-6 flex">
            {error ? (
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-2">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                  <div className="text-[#0a0a08] text-xl font-semibold font-inter text-center">
                    Error Generating Summary
                  </div>
                  <div className="text-[#7a7871] text-sm font-normal font-inter text-center">
                    {error}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-[#14997e] text-white rounded-md hover:bg-[#0f7a64] transition-colors"
                  >
                    Retry
                  </button>
                  <button
                    onClick={handleSkip}
                    className="px-4 py-2 bg-white border border-[#e7e6e3] text-[#7a7871] rounded-md hover:bg-[#f8f7f2] transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center gap-2">
                  <div className="text-[#0a0a08] text-xl font-semibold font-inter text-center">
                    Your first summary is being generated
                  </div>
                  <div className="text-[#7a7871] text-sm font-normal font-inter text-center">
                    This may take a few moments...
                  </div>
                </div>
                
                <div className="w-full max-w-md">
                  <ProgressBar progress={progress} subtitle={loadingTitle} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const GeneratingSummaryWrapper = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [hasInitiated, setHasInitiated] = useState(false);
  const { 
    isLoading, 
    setIsLoading, 
    loadingProgress, 
    userSummaries, 
    reloadUser,
    socket,
    user
  } = useUserContext();

  // Skip this page if user already has summaries
  useEffect(() => {
    if (userSummaries && userSummaries.length > 0) {
      navigate('/onboarding/success');
      return;
    }
  }, [userSummaries, navigate]);
  
  const loadingTitle = () => {
    if (loadingProgress < 6) {
      return "Generating summary: warming up..."
    }
    if (loadingProgress < 20) {
      return "Generating summary: loading newsletters..."
    }
    if (loadingProgress < 80) {
      return "Generating summary: summarizing articles..."
    }
    if (loadingProgress < 90) {
      return "Almost there..."
    }
    return "Finishing up..."
  }
  
  useEffect(() => {
    const initiateSummaryGeneration = async () => {
      if (!socket || !user) {
        return;
      }

      if (hasInitiated) {
        return;
      }

      // Don't initiate if user already has summaries
      if (userSummaries && userSummaries.length > 0) {
        return;
      }

      setHasInitiated(true);

      try {
        setIsLoading(true);
        setError(null);
        
        // Call the existing REST API endpoint
        const data = await sendRequest("/user_summaries/generate", "POST");
        
      } catch (error) {
        console.error("❌ Error initiating summary generation:", error);
        setError(error.message || "Failed to generate summary. Please try again.");
        setIsLoading(false);
        setHasInitiated(false); // Allow retry
      }
    };

    // Execute immediately, no delay
    initiateSummaryGeneration();
    
  }, [user, userSummaries, socket, setIsLoading, hasInitiated]); // Added socket and hasInitiated to dependencies

  // Listen for websocket error events
  useEffect(() => {
    if (!socket) return;

    const handleSummaryError = (data) => {
      console.error("Summary generation error:", data);
      setError(data.error || "Failed to generate summary. Please try again.");
      setIsLoading(false);
    };

    socket.on('summary_error', handleSummaryError);

    return () => {
      socket.off('summary_error', handleSummaryError);
    };
  }, [socket, setIsLoading]);

  // Check if summary generation is complete
  useEffect(() => {
    if (!isLoading && userSummaries && userSummaries.length > 0) {
      // Give a small delay before navigating to success
      setTimeout(() => {
        navigate('/onboarding/success');
      }, 500);
    }
  }, [isLoading, userSummaries, navigate]);

  return <GeneratingSummary 
    progress={loadingProgress} 
    error={error} 
    loadingTitle={loadingTitle()} 
  />;
};

export default GeneratingSummaryWrapper; 